import PropTypes from 'prop-types';
import React, { useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import styled, { css } from 'styled-components';
import {
  addHours,
  addDays,
  set,
  getHours,
  nextFriday,
  isEqual,
  isValid,
} from 'date-fns';
import ru from 'date-fns/locale/ru';
import { Remove } from '@icons/tagsselect';
import { formatDate } from '../../utils/date';

import GlobalStyles from '../../styles/components/datepicker';
import Button from '../Button';
import Input from '../Input';

const DatepickerCustomInput = React.forwardRef(
  (
    {
      isClearable,
      selectedValue,
      onInputClick,
      placeholder,
      multiple,
      formatDate,
      onReset,
    },
    ref
  ) => {
    const [isHovered, setIsHovered] = useState(false);

    let label;
    if (selectedValue) {
      if (multiple) {
        const [startDate, endDate] = selectedValue;
        label = isEqual(startDate, endDate)
          ? formatDate(startDate)
          : `${formatDate(startDate)} – ${formatDate(endDate)}`;
      } else {
        label = formatDate(selectedValue);
      }
    }

    return (
      <StyledButton
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={onInputClick}
        ref={ref}
        $hasValue={!!selectedValue}
      >
        {label || placeholder}
        {isClearable && isHovered && selectedValue && (
          <StyledRemove
            onClick={e => {
              e.stopPropagation();
              onReset();
            }}
          >
            <StyledRemoveIcon />
          </StyledRemove>
        )}
      </StyledButton>
    );
  }
);

class DatePicker extends React.PureComponent {
  static getShortcuts() {
    const today = new Date();
    const tomorrow = addDays(today, 1);

    return [
      { label: 'Через час', value: addHours(today, 1) },
      { label: 'Через 2 часа', value: addHours(today, 2) },
      {
        label: 'Вечером',
        value:
          getHours(today) > 18
            ? set(tomorrow, { hours: 18 })
            : set(today, { hours: 18, minutes: 0 }),
      },
      { label: 'Завтра', value: tomorrow },
      { label: 'Послезавтра', value: addDays(today, 2) },
      { label: 'На выходных', value: nextFriday(today) },
    ];
  }

  static isValid(date) {
    return isValid(date);
  }

  static isEqualDates(date1, date2) {
    return isEqual(date1, date2);
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    const { value, multiple } = nextProps;
    const { value: oldValue, initialValue, isFillingStart } = prevState;

    // Сброс значения извне
    if (value === null && oldValue !== null && !isFillingStart) {
      return { value: null, initialValue: null };
    }

    // Если новое value передали извне
    if (value && value !== initialValue) {
      if (multiple) {
        return {
          value: value,
          initialValue: value,
        };
      }
      return {
        value,
        initialValue: value,
      };
    }

    return null;
  }

  constructor(props) {
    super(props);

    const { value, forceShown, shortcuts: externalShortcuts } = props;
    this.state = {
      isOpen: forceShown,
      isFillingStart: forceShown,
      value,
      shortcuts: externalShortcuts || DatePicker.getShortcuts(),
      isHovered: false,
    };

    this.inputRef = React.createRef();

    this.formatDate = this.formatDate.bind(this);
    this.handleReset = this.handleReset.bind(this);
    this.handleTimeChange = this.handleTimeChange.bind(this);
    this.rendererContainer = this.rendererContainer.bind(this);
    this.shortcutsRenderer = this.shortcutsRenderer.bind(this);
    this.timePickerRenderer = this.timePickerRenderer.bind(this);
    this.toggleDatePicker = this.toggleDatePicker.bind(this);
    this.handleChange = this.handleChange.bind(this);
  }

  toggleDatePicker() {
    const { onChange } = this.props;
    const { value } = this.state;
    this.setState(
      prevState => ({
        isFillingStart: !prevState.isFillingStart,
        isOpen: !prevState.isOpen,
      }),
      () => {
        if (!this.state.isOpen) onChange(value);
      }
    );
  }

  handleReset() {
    const { isOpen } = this.state;
    const { onChange } = this.props;
    this.setState({ value: null }, () => {
      onChange(null);
      {
        isOpen && this.toggleDatePicker();
      }
    });
  }

  handleSubmit() {
    const { onChange } = this.props;
    const { value } = this.state;
    onChange(value);
    this.toggleDatePicker();
  }

  handleTimeChange(ev) {
    ev.stopPropagation();
    const { value: stateDate } = this.state;
    const { value } = ev.currentTarget;
    const [hours, minutes] = value.split(':');

    const selectedDate = stateDate || new Date();
    const newDate = set(selectedDate, { hours, minutes });

    this.setState({ value: newDate });
  }

  handleChangeShortcut(value) {
    this.setState({ value });
  }

  formatDate(date) {
    const { dateFormat } = this.props;
    return formatDate(date, dateFormat);
  }

  shortcutsRenderer() {
    const { shortcuts } = this.state;
    return (
      <StyledShortcuts>
        {shortcuts.map(({ label, value }) => (
          <StyledShortcut
            key={label}
            functional
            data-value={value}
            onClick={() => this.handleChangeShortcut(value)}
          >
            {label}
          </StyledShortcut>
        ))}
      </StyledShortcuts>
    );
  }

  timePickerRenderer() {
    const { value } = this.state;
    const date = value ? formatDate(value, 'HH:mm') : '';
    return (
      <StyledTime value={date} onChange={this.handleTimeChange} type="time" />
    );
  }

  rendererContainer({ className, children }) {
    const {
      multiple,
      required,
      showShortcuts,
      showTimeSelect,
      submitLabel,
      suffix,
    } = this.props;
    const { value } = this.state;
    const allowEmpty = !required && multiple;
    const buttonLabel =
      submitLabel && value
        ? formatDate(value, `${submitLabel} d MMMM, HH:mm`)
        : null;

    return (
      <StyledContainer className={className}>
        <StyledHead>
          {allowEmpty && this.resetRenderer()}
          {showShortcuts && this.shortcutsRenderer()}
          {showTimeSelect && this.timePickerRenderer()}
        </StyledHead>
        <StyledChildren>{children}</StyledChildren>
        {suffix}
        {buttonLabel && value && (
          <StyledBottom>
            <StyledSubmit onClick={this.toggleDatePicker}>
              {buttonLabel}
            </StyledSubmit>
          </StyledBottom>
        )}
      </StyledContainer>
    );
  }

  resetRenderer() {
    const { value } = this.state;
    return (
      <StyledResetButton checked={!value} onClick={this.handleReset}>
        Все даты
      </StyledResetButton>
    );
  }

  handleChange(dates) {
    const { onChange, multiple, showTimeSelect } = this.props;

    if (multiple) {
      this.setState({ value: dates }, () => {
        const [start, end] = dates || [];
        if (start && end) {
          onChange(dates);
          this.toggleDatePicker();
        }
      });
    } else if (showTimeSelect) {
      this.setState({ value: dates });
    } else {
      this.setState({ value: dates }, () => {
        onChange(dates);
        this.toggleDatePicker();
      });
    }
  }

  normalizeDate(date) {
    if (!date) return 'empty';
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  }

  render() {
    const { value, isOpen } = this.state;
    const {
      allowSameDay,
      forceShown,
      filterDate,
      multiple,
      minDate,
      maxDate,
      isClearable,
      showTimeSelect,
      placeholder: customPlaceholder,
      ...props
    } = this.props;

    let startDate = null;
    let endDate = null;
    if (multiple && Array.isArray(value)) [startDate, endDate] = value;
    else if (!multiple && value instanceof Date) startDate = value;

    const placeholder =
      customPlaceholder || (multiple ? 'Все даты' : 'Выбрать дату');

    const customInputElement = (
      <DatepickerCustomInput
        isClearable={isClearable}
        onInputClick={this.toggleDatePicker}
        ref={this.inputRef}
        multiple={multiple}
        formatDate={this.formatDate}
        selectedValue={value}
        onReset={this.handleReset}
      />
    );

    return (
      <StyledDatePicker $isInline={!!forceShown} {...props}>
        <GlobalStyles />
        <ReactDatePicker
          customInput={customInputElement}
          placeholderText={placeholder}
          allowSameDay={allowSameDay}
          calendarContainer={this.rendererContainer}
          selectsRange={multiple}
          startDate={startDate}
          endDate={endDate}
          filterDate={filterDate}
          inline={forceShown}
          open={!forceShown ? isOpen : undefined}
          // хак, чтобы календарь не ререндерился и не перескакивал на предыдущую дату
          key={this.normalizeDate(startDate || new Date())}
          isClearable={isClearable}
          locale={ru}
          minDate={minDate}
          maxDate={maxDate}
          selected={startDate}
          openToDate={startDate}
          onClickOutside={this.toggleDatePicker}
          onChange={this.handleChange}
          popperPlacement="bottom-start"
          popperModifiers={[
            {
              name: 'flip',
              options: {
                fallbackPlacements: ['top-start'],
              },
            },
            {
              name: 'preventOverflow',
              options: {
                padding: 8,
              },
            },
          ]}
        />
      </StyledDatePicker>
    );
  }
}

DatePicker.propTypes = {
  filterDate: PropTypes.func,
  allowSameDay: PropTypes.bool,
  className: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.instanceOf(Date),
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string.isRequired),
    PropTypes.arrayOf(PropTypes.instanceOf(Date)),
  ]),
  dateFormat: PropTypes.string,
  minDate: PropTypes.instanceOf(Date),
  maxDate: PropTypes.number,
  forceShown: PropTypes.bool,
  multiple: PropTypes.bool,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  showShortcuts: PropTypes.bool,
  showTimeSelect: PropTypes.bool,
  submitLabel: PropTypes.string,
  suffix: PropTypes.node,
  isClearable: PropTypes.bool,
};

DatePicker.defaultProps = {
  allowSameDay: true,
  className: '',
  dateFormat: 'd MMM yy',
  filterDate: null,
  forceShown: false,
  multiple: false,
  minDate: null,
  maxDate: null,
  onChange: Function.prototype,
  placeholder: null,
  required: true,
  showShortcuts: false,
  showTimeSelect: false,
  submitLabel: null,
  value: null,
  suffix: null,
  isClearable: true,
};

const StyledRemove = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;

  > svg {
    height: 10px;
    width: 10px;
    stroke: #007aff;
  }

  :hover {
    background-color: transparent !important;
  }
`;

const StyledRemoveIcon = styled(Remove)``;

const StyledDatePicker = styled.div`
  position: relative;

  .react-datepicker {
    ${({ $isInline }) =>
      $isInline &&
      css`
        position: absolute;
        left: 0;
        top: calc(100% + 5px);
      `}
    border: none;
    display: flex;
    flex-direction: column;
    border-radius: 0;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    z-index: 2;
  }

  .react-datepicker__month-container {
    padding: 5px 10px 10px;
  }

  .react-datepicker__close-icon {
    display: none;
  }

  .react-datepicker-wrapper {
    width: 100%;
  }

  .react-datepicker__input-container {
    width: 100%;
  }

  .react-datepicker__header {
    padding: 0;
    border: none;
    background: none;
    line-height: 48px;

    &:first-letter {
      text-transform: uppercase;
    }
  }

  .react-datepicker__current-month,
  .react-datepicker-time__header,
  .react-datepicker-year-header {
    color: #999;
    font-size: 19px;
    font-weight: normal;
    border-bottom: 3px solid #f8f8f8;
  }

  .react-datepicker__day--selected,
  .react-datepicker__day--in-selecting-range,
  .react-datepicker__day--in-range,
  .react-datepicker__month-text--selected,
  .react-datepicker__month-text--in-selecting-range,
  .react-datepicker__month-text--in-range {
    background-color: #ffedce;
    color: #000;
  }

  .react-datepicker__day-names {
    font-weight: bold;
    text-transform: uppercase;
  }

  .react-datepicker__day-names,
  .react-datepicker__week {
    font-size: 15px;
  }

  .react-datepicker__day-name,
  .react-datepicker__day {
    margin: 0;
    width: 35px;
    height: 35px;
    font-size: 15px;
    border-radius: 0;
    line-height: 35px;
    text-align: center;

    &--weekend {
      color: #bd6262;
    }

    &--selected {
      color: #fff;
      background-color: #595959;
    }

    &--disabled,
    &--outside-month {
      opacity: 0.3;
      background: none;
      pointer-events: none;
    }
  }

  .react-datepicker__month {
    margin: 0;
  }

  .react-datepicker__navigation {
    top: 18px;
    width: 15px;
    height: 15px;
    border-width: 0.5rem;

    &--previous {
      left: 13px;
    }

    &--next {
      right: 13px;
    }
  }

  .react-datepicker__triangle {
    display: none;
  }

  .react-datepicker__aria-live {
    display: none;
  }

  &:hover {
    ${StyledRemove} {
      display: flex;
    }
  }
`;

const StyledShortcuts = styled.div`
  display: flex;
  flex-flow: wrap;
  margin-bottom: 10px;
`;

const StyledShortcut = styled(Button)`
  width: 50%;
  text-align: left;
  font-size: 12px;
  line-height: 22px;
  outline: none;

  &:hover {
    color: #007aff;
  }
`;

const StyledContainer = styled.div`
  position: relative;
`;

const StyledChildren = styled.div`
  position: relative;
`;

const StyledHead = styled.div`
  padding: 10px 10px 0;
`;

const StyledBottom = styled.div`
  padding: 0 10px 10px;
`;

const StyledSubmit = styled(Button)`
  background: #007aff;
  height: 36px;
  color: #fff;
  padding: 0;
  text-align: center;
  width: 100%;

  &:hover {
    color: white;
  }
`;

export const StyledButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  height: 36px;
  width: 100%;
  border: none;
  font-size: 14px;
  text-align: left;
  color: ${({ $hasValue }) => ($hasValue ? '#4d4d4d' : '#92949a')};

  &:hover {
    color: ${({ $hasValue }) => ($hasValue ? '#4d4d4d' : '#92949a')};
  }
`;

const StyledResetButton = styled(Button)`
  width: 86px;
  height: 36px;
  font-size: 14px;
  border-radius: 18px;
  border: solid 1px #007aff;

  ${p =>
    p.checked &&
    `
    color: white;
    border-color: inherit;
    background-color: #007aff;

    &:hover {
      color: white;
      border-color: inherit;
    }
  `}
`;

const StyledTime = styled(Input)`
  width: 100%;
  padding: 0;
  border: none;
  font-size: 28px;
  outline: none;

  ${({ $isEditingTime }) =>
    $isEditingTime &&
    css`
      border-color: #007aff;
    `}

  ::-webkit-datetime-edit-fields-wrapper {
    display: flex;
  }

  ::-webkit-datetime-edit-text {
    padding: 4px 0;
    width: 32px;
    text-align: center;
  }

  ::-webkit-datetime-edit-hour-field,
  ::-webkit-datetime-edit-minute-field {
    background: white;
    border: 1px solid #e6e9ee;
    color: black;
    border-radius: 4px;
    padding: 5px 0;
    flex: 1;
    text-align: center;
  }

  ::-webkit-datetime-edit-hour-field:focus,
  ::-webkit-datetime-edit-minute-field:focus {
    color: black;
    background: white;
    border-color: #007aff;
  }

  :focus::-webkit-datetime-edit-hour-field,
  :focus::-webkit-datetime-edit-minute-field {
    border-color: #007aff;
  }

  ::-webkit-datetime-edit-ampm-field,
  ::-webkit-clear-button,
  ::-webkit-calendar-picker-indicator,
  ::-webkit-calendar-picker-indicator:focus,
  ::-webkit-inner-spin-button {
    display: none;
    -webkit-appearance: none;
  }
`;

DatePicker.StyledButton = StyledButton;

export default DatePicker;
